import { lazy as reactLazy } from "react"
import type { ComponentType, LazyExoticComponent } from "react"

/**
 * True when an error is a dynamic-import / chunk-load failure (as opposed to a
 * real runtime error inside the module). These are almost always deploy-window
 * transients: the server binary embeds the frontend, so a `systemctl restart`
 * leaves a ~1–2s gap where asset fetches fail, and a freshly-deployed build
 * renames hashed chunks a stale tab may still request.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: string } | undefined)?.message || err || "")
  return /dynamically imported module|importing a module script|failed to fetch dynamically|chunkloaderror|error loading dynamically imported|unable to preload|load failed|networkerror|failed to fetch/i.test(msg)
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/**
 * `React.lazy` with automatic retry for chunk-load failures.
 *
 * Native ESM re-attempts a dynamic import on each call (a *rejected* import is
 * not cached), so retrying the factory a few times over ~5s bridges the deploy
 * restart gap and gives the on-disk asset accumulator time to serve a renamed
 * chunk — the page recovers WITHOUT a disruptive reload. Only a genuinely
 * persistent chunk failure (or a real error inside the module) falls through to
 * the surrounding error boundary, which then reloads to fetch the new shell.
 *
 * Drop-in for `React.lazy`: `import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry"`.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return reactLazy(async () => {
    // Backoff spans the restart window: 0.4s + 0.9s + 1.6s + 2.5s ≈ 5.4s total.
    const DELAYS = [400, 900, 1600, 2500]
    let lastErr: unknown
    for (let attempt = 0; attempt <= DELAYS.length; attempt++) {
      try {
        return await factory()
      } catch (err) {
        lastErr = err
        // Don't retry a real module error — only transient chunk/network failures.
        if (!isChunkLoadError(err) || attempt === DELAYS.length) throw err
        // Offline (field worker on dropped LTE) → fail fast. Retrying for 5s
        // would just hang them; the error boundary shows a retry affordance and
        // the import naturally re-attempts when they navigate back online. Only
        // the deploy-transient case (online, but server mid-restart) benefits
        // from the backoff.
        if (typeof navigator !== "undefined" && navigator.onLine === false) throw err
        await sleep(DELAYS[attempt])
      }
    }
    throw lastErr
  })
}
